import { useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import type { Activity, PlannedWorkout } from "@/lib/types";
import { useUI } from "@/lib/theme";
import { addMonths, monthGrid, monthStart, toDateStr } from "@/lib/calendar";
import { MonthPicker } from "@/components/ui";
import ActivityCard from "@/components/calendar/ActivityCard";

const STATUS_BG: Record<string, string> = {
  published: "border-accent/30 bg-accent/5",
  done: "border-emerald-500/30 bg-emerald-500/10",
  skipped: "border-line bg-panel opacity-60",
};

const INTENSITY_LABEL: Record<string, string> = {
  easy: "Suave",
  moderate: "Moderado",
  hard: "Intenso",
  rest: "Descanso",
};

/** Modal de solo lectura para que el atleta vea el contenido de un entreno publicado. */
function PlanDetailModal({
  workout,
  onClose,
  onMark,
}: {
  workout: PlannedWorkout;
  onClose: () => void;
  onMark: (id: string, status: "done" | "skipped") => void;
}) {
  const { t, lang } = useUI();
  const p = workout.payload;
  const canMark = workout.status === "published";

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-line bg-panel shadow-2xl">
        <div className="flex items-start justify-between border-b border-line px-5 py-4">
          <h2 className="font-semibold text-fg">{p.title}</h2>
          <button
            onClick={onClose}
            className="ml-4 rounded-lg border border-line px-2 py-1 text-xs text-muted hover:text-fg"
          >
            ✕
          </button>
        </div>
        <div className="space-y-3 px-5 py-4 text-sm">
          {p.sport && (
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-muted">
                {t("cal_sport_label")}
              </span>
              <p className="mt-0.5 text-fg">{p.sport}</p>
            </div>
          )}
          {p.intensity && (
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-muted">
                {t("cal_intensity_label")}
              </span>
              <p className="mt-0.5 text-fg">
                {t(`cal_int_${p.intensity}`) || INTENSITY_LABEL[p.intensity] || p.intensity}
              </p>
            </div>
          )}
          {p.duration_min && (
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-muted">
                {t("cal_duration_label")}
              </span>
              <p className="mt-0.5 text-fg">{p.duration_min} min</p>
            </div>
          )}
          {p.description && (
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-muted">
                {t("cal_description_label")}
              </span>
              <p className="mt-0.5 whitespace-pre-wrap text-fg">{p.description}</p>
            </div>
          )}
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              {t("cal_status_label")}
            </span>
            <p className="mt-0.5 text-fg">{t(`cal_status_${workout.status}`)}</p>
          </div>
        </div>
        {canMark && (
          <div className="flex gap-2 border-t border-line px-5 py-3">
            <button
              onClick={() => { onMark(workout.id, "done"); onClose(); }}
              className="flex-1 rounded-xl border border-emerald-500/40 bg-emerald-500/10 py-2 text-sm font-semibold text-emerald-600 hover:bg-emerald-500/20 dark:text-emerald-400"
            >
              {t("cal_mark_done")}
            </button>
            <button
              onClick={() => { onMark(workout.id, "skipped"); onClose(); }}
              className="flex-1 rounded-xl border border-line py-2 text-sm font-medium text-muted hover:text-fg"
            >
              {t("cal_mark_skipped")}
            </button>
          </div>
        )}
        <div className="px-5 pb-4">
          <button
            onClick={onClose}
            className="w-full rounded-xl border border-line py-2 text-sm text-muted hover:text-fg"
          >
            {t("cal_cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}

function PlanCard({
  workout,
  matched,
  onOpen,
}: {
  workout: PlannedWorkout;
  matched: boolean;
  onOpen: (w: PlannedWorkout) => void;
}) {
  const { t, lang } = useUI();
  const p = workout.payload;
  return (
    <div
      className={`rounded border p-1.5 text-[11px] cursor-pointer hover:brightness-95 transition ${STATUS_BG[workout.status] ?? "border-line bg-panel"}`}
      onClick={() => onOpen(workout)}
      title={lang === "es" ? "Ver entreno" : "View workout"}
    >
      <p className="truncate font-semibold leading-snug">{p.title}</p>
      {matched && (
        <p className="text-[10px] text-emerald-600 dark:text-emerald-400">✓ {t("cal_activity_done")}</p>
      )}
    </div>
  );
}

export default function Calendario({
  acts,
  onOpenActivity,
}: {
  acts: Activity[];
  onOpenActivity?: (categoria: string) => void;
}) {
  const { t, lang: ui_lang } = useUI();
  const [month, setMonth] = useState(() => monthStart(new Date()));
  const [workouts, setWorkouts] = useState<PlannedWorkout[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailWorkout, setDetailWorkout] = useState<PlannedWorkout | null>(null);

  const grid = monthGrid(month);
  const from = toDateStr(grid[0]![0]!);
  const to   = toDateStr(grid[grid.length - 1]![6]!);

  const load = useCallback(() => {
    setLoading(true);
    api
      .calendar(from, to)
      .then((r) => setWorkouts(r.workouts))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const workoutsByDate = new Map<string, PlannedWorkout[]>();
  for (const w of workouts) {
    const arr = workoutsByDate.get(w.scheduled_date) ?? [];
    arr.push(w);
    workoutsByDate.set(w.scheduled_date, arr);
  }

  // Actividades reales indexadas por fecha local
  const actsByDate = new Map<string, Activity[]>();
  for (const a of acts) {
    const ds = a.start_local.slice(0, 10);
    const arr = actsByDate.get(ds) ?? [];
    arr.push(a);
    actsByDate.set(ds, arr);
  }

  const today     = toDateStr(new Date());
  const thisMonth = month.getMonth();

  const handleMark = async (id: string, status: "done" | "skipped") => {
    await api.updateCalendarWorkout(id, status).catch(() => {});
    setWorkouts((prev) => prev.map((w) => (w.id === id ? { ...w, status } : w)));
  };

  const DAY_ABBR =
    ui_lang === "es"
      ? ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"]
      : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div>
      {/* Navegación de mes — card unificada */}
      <div className="mb-4 flex items-center gap-2 rounded-xl border border-line bg-panel px-3 py-2 shadow-sm">
        <button
          onClick={() => setMonth((m) => addMonths(m, -1))}
          className="rounded-lg border border-line px-3 py-1.5 text-sm font-medium text-muted transition hover:bg-panel2 hover:text-fg"
        >
          ‹ {t("cal_month_prev")}
        </button>
        <div className="flex flex-1 justify-center">
          <MonthPicker
            value={month}
            onChange={setMonth}
            todayLabel={t("cal_today")}
            openLabel={t("cal_open_picker")}
            lang={ui_lang}
          />
        </div>
        <button
          onClick={() => setMonth((m) => addMonths(m, 1))}
          className="rounded-lg border border-line px-3 py-1.5 text-sm font-medium text-muted transition hover:bg-panel2 hover:text-fg"
        >
          {t("cal_month_next")} ›
        </button>
      </div>

      {/* Cabecera de días */}
      <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] font-semibold text-muted">
        {DAY_ABBR.map((d) => <div key={d}>{d}</div>)}
      </div>

      {/* Rejilla mensual */}
      <div className="grid grid-cols-7 gap-1">
        {grid.flatMap((week) =>
          week.map((day) => {
            const ds        = toDateStr(day);
            const isToday   = ds === today;
            const isOther   = day.getMonth() !== thisMonth;
            const dayPlan   = workoutsByDate.get(ds) ?? [];
            const dayActs   = actsByDate.get(ds) ?? [];

            // Entrenos published que tienen actividad real el mismo día
            const matchedIds = new Set<string>();
            if (dayPlan.length > 0 && dayActs.length > 0) {
              dayPlan
                .filter((w) => w.status === "published")
                .forEach((w) => matchedIds.add(w.id));
            }

            return (
              <div
                key={ds}
                className={`min-h-[104px] rounded-xl border p-2 transition
                  ${isToday
                    ? "border-accent/60 bg-accent/10 ring-1 ring-accent/30"
                    : isOther
                      ? "border-line/30 bg-panel2/15 opacity-50"
                      : "border-line bg-panel/60"}`}
              >
                <p className={`mb-1 text-left text-[13px] font-bold ${isToday ? "text-accent" : isOther ? "text-muted/60" : "text-fg/80"}`}>
                  {day.getDate()}
                </p>

                {loading && dayPlan.length === 0 && dayActs.length === 0 && (
                  <div className="h-4 animate-pulse rounded bg-panel2" />
                )}

                {/* Entrenos planificados */}
                <div className="space-y-1">
                  {dayPlan.map((w) => (
                    <PlanCard
                      key={w.id}
                      workout={w}
                      matched={matchedIds.has(w.id)}
                      onOpen={setDetailWorkout}
                    />
                  ))}
                </div>

                {/* Actividades reales como banner */}
                <div className="mt-1 space-y-1">
                  {dayActs.map((a) => (
                    <ActivityCard
                      key={a.id}
                      onOpen={onOpenActivity}
                      openHint={ui_lang === "es" ? "Ver en su disciplina" : "View in its discipline"}
                      act={{
                        id: a.id,
                        categoria: a.categoria,
                        sport_type: a.sport_type,
                        name: a.name,
                        start_local: ds,
                        duration_min: a.duration_min ?? null,
                        distance_km: a.distance_km ?? null,
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal de detalle del plan (solo lectura) */}
      {detailWorkout && (
        <PlanDetailModal
          workout={detailWorkout}
          onClose={() => setDetailWorkout(null)}
          onMark={handleMark}
        />
      )}
    </div>
  );
}
